/**
 * CurrencyContext.jsx
 * ───────────────────
 * Global currency preference manager.
 *
 * Features:
 *  1. Per-user scoped localStorage key   → different users on same device keep
 *     their own preference (key: `user_currency_<userId>`)
 *  2. Cross-tab synchronisation          → `storage` event propagates changes
 *     to all open tabs immediately
 *  3. Live exchange rates                → fetched from exchangerate.host (free,
 *     no API key needed) with 1-hour TTL cache in localStorage
 *  4. convertAmount(amount, from, to)    → convert any amount between any two
 *     supported currencies (defaults: from=USD, to=activeCurrency)
 *  5. formatCompact(amount)              → compact notation ($1.2K, €4.5M)
 *  6. currencyChanged CustomEvent        → fired on window for non-React consumers
 *     (e.g. vanilla JS PDF generators, analytics scripts)
 *  7. Auth-aware                         → listens to ncb_user localStorage
 *     changes so switching accounts instantly loads the correct preference
 */

import React, {
  createContext, useContext, useState, useEffect, useCallback, useRef,
} from 'react';
import { getSession } from '../api';

// ── Supported currencies ──────────────────────────────────────────────────────
const SUPPORTED_CURRENCIES = {
  USD: { code: 'USD', symbol: '$',   name: 'US Dollar',              locale: 'en-US' },
  EUR: { code: 'EUR', symbol: '€',   name: 'Euro',                   locale: 'de-DE' },
  GBP: { code: 'GBP', symbol: '£',   name: 'British Pound',          locale: 'en-GB' },
  CAD: { code: 'CAD', symbol: 'C$',  name: 'Canadian Dollar',        locale: 'en-CA' },
  AUD: { code: 'AUD', symbol: 'A$',  name: 'Australian Dollar',      locale: 'en-AU' },
  NGN: { code: 'NGN', symbol: '₦',   name: 'Nigerian Naira',         locale: 'en-NG' },
  XAF: { code: 'XAF', symbol: 'CFA', name: 'CFA Franc (Central)',    locale: 'fr-CM' },
  CNY: { code: 'CNY', symbol: '¥',   name: 'Chinese Yuan',           locale: 'zh-CN' },
  XOF: { code: 'XOF', symbol: 'CFA', name: 'West African CFA Franc', locale: 'fr-SN' },
  JPY: { code: 'JPY', symbol: '¥',   name: 'Japanese Yen',           locale: 'ja-JP' },
};

// ── Constants ─────────────────────────────────────────────────────────────────
const RATES_CACHE_KEY  = 'ofl_exchange_rates_cache';
const RATES_TTL_MS     = 60 * 60 * 1000;           // 1 hour
const LEGACY_KEY       = 'user_currency';           // backward-compat
const CUSTOM_EVENT     = 'currencyChanged';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Build a per-user localStorage key; falls back to the legacy global key. */
function storageKey(userId) {
  return userId ? `user_currency_${userId}` : LEGACY_KEY;
}

/** Detect a sensible default from the browser locale. */
function getBrowserDefault() {
  try {
    const l = navigator.language || 'en-US';
    if (l.startsWith('zh'))                                                    return 'CNY';
    if (l.startsWith('ja'))                                                    return 'JPY';
    if (l.includes('GB'))                                                      return 'GBP';
    if (l.includes('CA'))                                                      return 'CAD';
    if (l.includes('AU'))                                                      return 'AUD';
    if (l.includes('NG'))                                                      return 'NGN';
    if (l.includes('SN') || l.includes('CI') || l.includes('BF'))             return 'XOF';
    if (l.includes('CM') || l.includes('GA') || l.includes('CG'))             return 'XAF';
    if (['de', 'fr', 'es', 'it', 'nl', 'be'].some(p => l.startsWith(p)))     return 'EUR';
    return 'USD';
  } catch { return 'USD'; }
}

/** Read + validate a saved currency code from localStorage. */
function readSaved(userId) {
  // 1. Try the per-user key
  const perUser = localStorage.getItem(storageKey(userId));
  if (perUser && SUPPORTED_CURRENCIES[perUser]) return perUser;

  // 2. Migrate legacy 'CAF' → 'XAF'
  const legacy = localStorage.getItem(LEGACY_KEY);
  if (legacy === 'CAF') {
    localStorage.setItem(LEGACY_KEY, 'XAF');
    return 'XAF';
  }
  if (legacy && SUPPORTED_CURRENCIES[legacy]) return legacy;

  return getBrowserDefault();
}

/** Persist currency for a user (and keep legacy key in sync). */
function persist(code, userId) {
  localStorage.setItem(storageKey(userId), code);
  localStorage.setItem(LEGACY_KEY, code);     // keep legacy consumers working
}

// ── Exchange rate cache ───────────────────────────────────────────────────────
function loadRatesCache() {
  try {
    const raw = localStorage.getItem(RATES_CACHE_KEY);
    if (!raw) return null;
    const { rates, fetchedAt } = JSON.parse(raw);
    if (Date.now() - fetchedAt > RATES_TTL_MS) return null;   // expired
    return rates;
  } catch { return null; }
}

function saveRatesCache(rates) {
  try {
    localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({ rates, fetchedAt: Date.now() }));
  } catch { /* storage quota — silent */ }
}

/** Fetch USD-based exchange rates from exchangerate.host (free, no key). */
async function fetchRates() {
  const cached = loadRatesCache();
  if (cached) return cached;

  try {
    const res  = await fetch('https://api.exchangerate-api.com/v4/latest/USD', {
      signal: AbortSignal.timeout(6000),
    });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const { rates } = await res.json();
    if (rates && typeof rates === 'object') {
      saveRatesCache(rates);
      return rates;
    }
  } catch (err) {
    console.warn('[CurrencyContext] Exchange rate fetch failed:', err.message);
  }

  // Approximate static fallback (updated ~2025)
  const fallback = {
    USD: 1, EUR: 0.92, GBP: 0.79, CAD: 1.36, AUD: 1.53,
    NGN: 1610, XAF: 603, CNY: 7.24, XOF: 603, JPY: 149,
  };
  saveRatesCache(fallback);
  return fallback;
}

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────
const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
  const [userId,   setUserId]   = useState(() => getSession()?.id ?? null);
  const [currency, setCurrency] = useState(() => readSaved(getSession()?.id ?? null));
  const [rates,    setRates]    = useState({});
  const [ratesReady, setRatesReady] = useState(false);
  const prevUserRef = useRef(userId);

  // ── Load exchange rates on mount ──────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    fetchRates().then(r => {
      if (!cancelled) { setRates(r); setRatesReady(true); }
    });
    return () => { cancelled = true; };
  }, []);

  // ── Auth change: watch ncb_user in localStorage ───────────────────────────
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'ncb_user') {
        const session = e.newValue ? (() => { try { return JSON.parse(e.newValue); } catch { return null; } })() : null;
        const newId   = session?.id ?? null;
        if (newId !== prevUserRef.current) {
          prevUserRef.current = newId;
          setUserId(newId);
          setCurrency(readSaved(newId));
        }
      }
      // Cross-tab currency sync: another tab changed the per-user key
      if (e.key && e.key.startsWith('user_currency') && e.newValue) {
        const code = e.newValue;
        if (SUPPORTED_CURRENCIES[code]) setCurrency(code);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // ── changeCurrency ────────────────────────────────────────────────────────
  const changeCurrency = useCallback((code) => {
    if (!SUPPORTED_CURRENCIES[code]) return;
    setCurrency(code);
    persist(code, userId);
    // Notify non-React consumers
    window.dispatchEvent(new CustomEvent(CUSTOM_EVENT, { detail: { currency: code, userId } }));
  }, [userId]);

  // ── formatCurrency ────────────────────────────────────────────────────────
  const formatCurrency = useCallback((amount, overrideCurrency) => {
    const code   = overrideCurrency ?? currency;
    const config = SUPPORTED_CURRENCIES[code] ?? SUPPORTED_CURRENCIES.USD;
    const num    = Number(amount) || 0;
    try {
      return new Intl.NumberFormat(config.locale, {
        style: 'currency', currency: config.code,
      }).format(num);
    } catch {
      return `${config.symbol}${num.toLocaleString()}`;
    }
  }, [currency]);

  // ── formatCompact — compact notation ($1.2K, €4.5M) ──────────────────────
  const formatCompact = useCallback((amount, overrideCurrency) => {
    const code   = overrideCurrency ?? currency;
    const config = SUPPORTED_CURRENCIES[code] ?? SUPPORTED_CURRENCIES.USD;
    const num    = Number(amount) || 0;
    try {
      return new Intl.NumberFormat(config.locale, {
        style: 'currency', currency: config.code,
        notation: 'compact', compactDisplay: 'short',
        maximumFractionDigits: 1,
      }).format(num);
    } catch {
      // Manual fallback
      if (Math.abs(num) >= 1_000_000) return `${config.symbol}${(num / 1_000_000).toFixed(1)}M`;
      if (Math.abs(num) >= 1_000)     return `${config.symbol}${(num / 1_000).toFixed(1)}K`;
      return `${config.symbol}${num.toLocaleString()}`;
    }
  }, [currency]);

  // ── convertAmount — convert between any two currencies ────────────────────
  /**
   * Convert `amount` from `fromCode` (default: 'USD') to `toCode`
   * (default: active currency). Uses cached USD-based rates.
   *
   * @param {number} amount
   * @param {string} [fromCode='USD']
   * @param {string} [toCode=currency]
   * @returns {number}
   */
  const convertAmount = useCallback((amount, fromCode = 'USD', toCode) => {
    const target = toCode ?? currency;
    if (!ratesReady || fromCode === target) return Number(amount);
    const fromRate = rates[fromCode] ?? 1;
    const toRate   = rates[target]   ?? 1;
    // Convert to USD first, then to target
    const inUSD    = Number(amount) / fromRate;
    return inUSD * toRate;
  }, [currency, rates, ratesReady]);

  // ── getExchangeRate — raw rate helper ─────────────────────────────────────
  const getExchangeRate = useCallback((fromCode = 'USD', toCode) => {
    const target = toCode ?? currency;
    if (!ratesReady) return 1;
    const fromRate = rates[fromCode] ?? 1;
    const toRate   = rates[target]   ?? 1;
    return toRate / fromRate;
  }, [currency, rates, ratesReady]);

  // ── refreshRates — manual refresh ─────────────────────────────────────────
  const refreshRates = useCallback(async () => {
    // Bust cache and re-fetch
    localStorage.removeItem(RATES_CACHE_KEY);
    const fresh = await fetchRates();
    setRates(fresh);
    setRatesReady(true);
    return fresh;
  }, []);

  // ── Context value ─────────────────────────────────────────────────────────
  const value = {
    /** Active currency code, e.g. 'USD' */
    currency,
    /** Full details for the active currency */
    currencyDetails: SUPPORTED_CURRENCIES[currency],
    /** All supported currencies as a flat array */
    supportedCurrencies: Object.entries(SUPPORTED_CURRENCIES).map(([key, c]) => ({
      ...c, displayCode: key,
    })),
    /** Change the active currency (persists per user) */
    changeCurrency,
    /** Format a number as a currency string using the active currency */
    formatCurrency,
    /** Compact notation: $1.2K, €4.5M */
    formatCompact,
    /**
     * Convert an amount between currencies.
     * convertAmount(99.99)                  → USD → active currency
     * convertAmount(99.99, 'EUR', 'NGN')    → EUR → NGN
     */
    convertAmount,
    /** Get raw exchange rate between two currencies */
    getExchangeRate,
    /** Whether live exchange rates have been loaded */
    ratesReady,
    /** Manually bust the rate cache and re-fetch */
    refreshRates,
    /** Raw rates object { USD: 1, EUR: 0.92, ... } */
    exchangeRates: rates,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) throw new Error('useCurrency must be used within a CurrencyProvider');
  return context;
};

export default CurrencyContext;